import {
    CurrencyEnum,
    DocumentTypeEnum,
    Employee,
    Expense,
    ExpenseKindEnum,
    ExpenseSplit,
    InvoiceSplit,
    ItemSplit,
    OcrResult,
    SplitTypeEnum,
    Tax,
    Vendor,
} from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { ItemSplitKindEnum, LineItem } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { OcrResultItem } from "@finway-group/shared/lib/models/ocr/ocr.model"
import { UserLanguageEnum } from "@finway-group/shared/lib/models/user/userSettings.interface"
import { formatCurrencyNumber, toAmountForCurrency, toDinero } from "@finway-group/shared/lib/utils"
import moment from "moment"
import * as stringSimilarity from "string-similarity"
import { DE_DATE_FORMAT, NO_TAX_RATE, US_DATE_FORMAT } from "~Shared/config/consts"
import { type VendorReadFileAiResponse } from "~Shared/services/ocr.service"
import { checkIsPurchaseRequest, isHospitalityExpense } from "~Shared/utils/expense.utils"
import { capitalizeFirstLetter, isEmptyObject, isSet } from "~Shared/utils/helper.utils"
import type {
    CompareDataParams,
    CompareFormOcrDataParams,
    CompareSplitsDataParams,
    HospitalityWithLabeledAttendees,
    MapSplitsParams,
    OcrObjectKeys,
    OcrObjectSplitsKeys,
    OcrSettings,
    RowInterface,
    SplitDataType,
    UpdateOcrItemsParams,
    UpdatedOcrObject,
} from "./ocr.utils.types"

const ITEM_FIELDS_TO_BE_COMPARED: Array<OcrObjectSplitsKeys> = ["name", "netPrice", "grossPrice", "taxRate", "taxPrice", "unitPrice"]

export const formatOCRObject = (ocrObject: OcrResult) => {
    const ocrObjectKeys = Object.keys(ocrObject) as Array<OcrObjectKeys>

    const newOcrObject = ocrObject as unknown as UpdatedOcrObject

    ocrObjectKeys.forEach((ocrKey) => {
        switch (ocrKey) {
            case "invoiceDate":
                newOcrObject[ocrKey] = newOcrObject[ocrKey] ? moment(newOcrObject[ocrKey]) : undefined
                break
            case "invoiceDueDate":
                newOcrObject[ocrKey] = moment(newOcrObject[ocrKey])
                break
            case "performancePeriod":
                newOcrObject[ocrKey] = newOcrObject[ocrKey]?.map((date) => moment(date))
                break
            case "datePurchased":
                newOcrObject[ocrKey] = moment(newOcrObject[ocrKey])
                break
            default:
                break
        }
    })

    return newOcrObject
}

export const formatFieldValue = ({
    field,
    value,
    currency,
    userLanguage = UserLanguageEnum.DE,
}: {
    field: OcrObjectKeys | OcrObjectSplitsKeys
    value: any
    currency: CurrencyEnum
    userLanguage?: UserLanguageEnum
}) => {
    if (!isSet(value) && field !== "taxRate") {
        return ""
    }

    switch (field) {
        case "invoiceNumber":
            return `#${value}`
        case "invoiceDate":
            return formatOcrDate(value, userLanguage)
        case "invoiceDueDate":
            return formatOcrDate(value, userLanguage)
        case "totalNetPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "totalTaxPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "totalGrossPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "taxRate":
            if (!value || value._id === NO_TAX_RATE) {
                return "-"
            }
            return `${value.taxRate}%`
        case "taxPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "grossPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "netPrice":
            return formatCurrencyNumber({ value: Number(value), currency })
        case "performancePeriod":
            if (value.includes(undefined) || !value.length) {
                return "-"
            }
            return `${formatOcrDate(value[0], userLanguage)} - ${formatOcrDate(value[1], userLanguage)}`
        case "datePurchased":
            return formatOcrDate(value, userLanguage)
        case "name":
            return capitalizeFirstLetter(value)
        case "vendor":
            return value.name
        default:
            return String(value)
    }
}

export const formatOcrDate = (value: string | Date, userLanguage: UserLanguageEnum) => moment(value).format(userLanguage === UserLanguageEnum.DE ? DE_DATE_FORMAT : US_DATE_FORMAT)

export const compareSplits = ({ originalSplits, ocrItems, t, newCurrency, originalCurrency }: MapSplitsParams): Array<RowInterface> => {
    const ocrItemsNames = ocrItems.map((item: ItemSplit) => item.name?.toLowerCase())

    const splitsData: Array<RowInterface> = originalSplits.map((originalSplit, itemIndex) => {
        let item: RowInterface = {} as RowInterface

        const ocrBestMatching = ocrItemsNames.length && "name" in originalSplit ? stringSimilarity.findBestMatch(originalSplit.name?.toLowerCase(), ocrItemsNames) : undefined
        const ocrBestMatchingName = ocrBestMatching?.bestMatch?.target?.toLowerCase()
        const ocrBestMatchingRating = ocrBestMatching?.bestMatch?.rating ?? 0

        for (const ocrItem of ocrItems) {
            const areSplitsMatching = ocrItem?.name?.toLowerCase() === ocrBestMatchingName && ocrBestMatchingRating >= 0.6

            // if the OCR split is matching with an original split, they should be compared
            if (areSplitsMatching) {
                const itemParent = compareSplitsData({ field: "itemParent", originalSplit, ocrItem, newCurrency, originalCurrency, itemIndex, t })

                const itemChildren: Array<RowInterface> = []

                for (const field of ITEM_FIELDS_TO_BE_COMPARED) {
                    const comparedData = compareSplitsData({ field, originalSplit, ocrItem, newCurrency, originalCurrency, itemIndex, t })
                    if (comparedData) {
                        itemChildren.push(comparedData)
                    }
                }

                if (itemChildren.length) {
                    if (itemParent) {
                        item = { ...itemParent, children: itemChildren }
                    }
                }
            }
        }

        return { ...item }
    })

    return splitsData
}

export const compareSplitsData = ({ field, originalSplit, ocrItem, newCurrency, originalCurrency, itemIndex, t }: CompareSplitsDataParams): RowInterface | void => {
    let originalData: any = originalSplit[field as keyof ExpenseSplit]
    let ocrData: any = ocrItem[field as keyof ExpenseSplit]

    if (field === "itemParent") {
        originalData = "name" in originalSplit ? originalSplit.name : ""
        ocrData = ""
    }

    const formattedOcrData = formatFieldValue({ field, value: ocrData, currency: newCurrency })
    const formattedOriginalData = formatFieldValue({ field, value: originalData, currency: originalCurrency })
    const formattedField = t(`info:ocr_updates.fields_names.${field}`)

    const isDifferent = compareOcrAndOriginalData(formattedOriginalData, formattedOcrData)

    if (originalData !== undefined && ocrData !== undefined && isDifferent) {
        return {
            key: itemIndex + field,
            field,
            ocrData,
            itemIndex,
            formattedOriginalData,
            formattedOcrData,
            formattedField,
            split: true,
        }
    }
}

export const compareData = (
    { field, expense, ocrObject, foundVendor, newCurrency, originalCurrency, t, isHospitality, isShowingVendorChangeIfNotFound }: CompareDataParams,
    userLanguage: UserLanguageEnum | undefined,
): RowInterface | void => {
    let originalData = expense[field as keyof Expense]
    let ocrData: any = ocrObject[field as keyof OcrResult]
    let formattedField = t(`info:ocr_updates.fields_names.${field}`)

    if (field === "performancePeriod") {
        originalData = originalData || [expense.performancePeriodStartDate, expense.performancePeriodEndDate]
    }

    if (field === "datePurchased") {
        formattedField = t(`info:ocr_updates.fields_names.${isHospitality ? "hospitalityDate" : "performanceDate"}`)
    }

    if (field === "vendor") {
        const expenseCreditorName = expense.getCreditorName()
        if (expenseCreditorName) {
            originalData = { name: expenseCreditorName }
        }

        if (foundVendor) {
            ocrData = { name: foundVendor.name }
        } else if (!isShowingVendorChangeIfNotFound) {
            ocrData = null
        }
    }

    if (field === "merchant") {
        ocrData = ocrData || ocrObject.vendor?.name
    }

    if (field === "taxRate") {
        originalData = expense.taxRate
        ocrData = ocrObject?.taxRate ?? { _id: NO_TAX_RATE }
    }

    const formattedOcrData = formatFieldValue({ field, value: ocrData, currency: newCurrency, userLanguage })
    const formattedOriginalData = formatFieldValue({ field, value: originalData, currency: originalCurrency, userLanguage })

    const isDifferent = compareOcrAndOriginalData(formattedOriginalData, formattedOcrData)

    if (isSet(ocrData) && isDifferent) {
        return {
            key: field,
            field,
            ocrData,
            formattedOriginalData,
            formattedOcrData,
            formattedField,
        }
    }
}

export const compareFormOcrData = ({ expense, ocrObject, foundVendor, t, isShowingVendorChangeIfNotFound }: CompareFormOcrDataParams, loggedInUserLanguage: UserLanguageEnum) => {
    const originalCurrency = expense.currency ?? CurrencyEnum.EUR
    const newCurrency = ocrObject.currency ?? originalCurrency

    const isAutoGeneratedCardRequest = expense.isAutoGenerated && expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD
    const isHospitality = isHospitalityExpense(expense)

    // Set the invoice and vendor fields that need to be compared
    const invoiceAndVendorFieldsToBeCompared: Array<OcrObjectKeys> = [
        "invoiceNumber",
        "currency",
        "invoiceDate",
        "invoiceDueDate",
        "vendor",
        "performancePeriod",
        "datePurchased",
        "description",
    ]

    // For autoGenerated card requests we disregard the OCR amount prices and tax rate
    const areTotalGrossPricesEqual = Math.abs(expense.totalGrossPrice) === Math.abs(ocrObject.totalGrossPrice ?? 0)
    if (!isAutoGeneratedCardRequest || areTotalGrossPricesEqual) {
        invoiceAndVendorFieldsToBeCompared.push("totalNetPrice", "totalTaxPrice", "totalGrossPrice", "taxRate")
    }
    if (isHospitality) {
        invoiceAndVendorFieldsToBeCompared.push("merchant")
    }

    const formOCRDataDifferences: Array<RowInterface> = []

    const originalSplits = expense.splits
    const ocrItems = ocrObject.splits as Array<ItemSplit>

    const shouldUpdateItems = ocrItems && expense.kind === (isHospitality ? ExpenseKindEnum.HOSPITALITY : ExpenseKindEnum.ONE_TIME_EXPENSE)

    if (shouldUpdateItems) {
        const splits = compareSplits({ originalSplits, ocrItems, t, newCurrency, originalCurrency }).filter((item) => !isEmptyObject(item))
        if (splits?.length) {
            formOCRDataDifferences.push(...splits)
        }
    }

    for (const field of invoiceAndVendorFieldsToBeCompared) {
        const comparedData = compareData(
            { field, expense, ocrObject, foundVendor, newCurrency, originalCurrency, t, isHospitality, isShowingVendorChangeIfNotFound },
            loggedInUserLanguage,
        )
        if (comparedData) {
            formOCRDataDifferences.push(comparedData)
        }
    }

    if (formOCRDataDifferences.length === 0) {
        return
    }

    const sortedDataDifference = formOCRDataDifferences.sort((a, _b) => (a.field === "itemParent" ? 1 : -1))

    return sortedDataDifference
}

export const mergeExistingAndOcrItems = (existingItems: Array<ExpenseSplit>, ocrItems: Array<OcrResultItem>) => {
    const noEmptyExistingItems = existingItems.filter((item: ItemSplit) => item.netPrice !== 0)
    const expenseItemsNames = noEmptyExistingItems.map((split: ItemSplit) => split.name?.toLowerCase())
    const notRepeatedOcrDataItems = ocrItems.filter((item: ItemSplit) => !expenseItemsNames.includes(item.name?.toLowerCase()))
    const tipItems = existingItems.filter((item: ItemSplit) => item.kind === ItemSplitKindEnum.TIP)

    return [...noEmptyExistingItems, ...notRepeatedOcrDataItems, ...tipItems]
}

export const compareOcrAndOriginalData = (originalData: string, ocrData: string) => originalData?.toLowerCase() !== ocrData?.toLowerCase()

export const updateOcrItems = ({ ocrItemizationEnabled, ocrObject, expense, isHospitality }: UpdateOcrItemsParams): SplitDataType => {
    const shouldUpdateItems = ocrItemizationEnabled && ocrObject.splits && expense.kind === (isHospitality ? ExpenseKindEnum.HOSPITALITY : ExpenseKindEnum.ONE_TIME_EXPENSE)

    if (shouldUpdateItems) {
        const isPurchaseRequest = checkIsPurchaseRequest(expense)
        if (expense.splitType === SplitTypeEnum.ITEM || expense.kind === ExpenseKindEnum.HOSPITALITY || expense.isReimbursement || isPurchaseRequest) {
            return {
                splitType: SplitTypeEnum.ITEM,
                splits: ocrObject.splits?.map((split: ItemSplit) => ({
                    _id: split._id,
                    taxRate: split.taxRate,
                    netPrice: split.netPrice,
                    taxPrice: split.taxPrice,
                    grossPrice: split.grossPrice,

                    ...(expense.kind !== ExpenseKindEnum.HOSPITALITY && {
                        costCenter: expense.costCenter,
                        costCenter2: expense.costCenter2,
                        expenseAccount: expense.expenseAccount,
                    }),
                    // specific to item split
                    name: split.name ?? "Item",
                    kind: split.kind,
                })),
            }
        }

        return {
            splitType: SplitTypeEnum.LINE_ITEM,
            splits: ocrObject.splits
                ?.filter((split) => !("percentage" in split))
                .map((split: LineItem) => ({
                    _id: split._id,
                    taxRate: split.taxRate,
                    netPrice: split.netPrice,
                    taxPrice: split.taxPrice,
                    grossPrice: split.grossPrice,

                    costCenter: expense.costCenter,
                    costCenter2: expense.costCenter2,
                    expenseAccount: expense.expenseAccount,
                    // specific to line item split
                    name: split.name ?? "Item",
                    quantity: split.quantity,
                    unitOfMeasure: split.unitOfMeasure,
                    unitPrice: split.unitPrice,
                    materialCode: split.materialCode,
                    purchaseOrderLink: split.purchaseOrderLink,
                })),
        }
    }

    if ([SplitTypeEnum.LINE_ITEM, SplitTypeEnum.ITEM].includes(expense.splitType)) {
        return undefined
    }
    return {
        splits: expense.splits?.map((split: InvoiceSplit) => ({
            ...split,
            netPrice: (ocrObject.totalNetPrice ?? expense.totalNetPrice ?? 0) * (split.percentage / 100),
            taxPrice: (ocrObject.totalTaxPrice ?? expense.totalTaxPrice ?? 0) * (split.percentage / 100),
            taxRate: ocrObject.taxRate ? (ocrObject.taxRate as InvoiceSplit["taxRate"]) : expense.taxRate ? expense.taxRate : { _id: NO_TAX_RATE },
            grossPrice: (ocrObject.totalGrossPrice ?? expense.totalGrossPrice ?? 0) * (split.percentage / 100),
        })),
    }
}

export const updatingAttendees = (existingData: Expense | HospitalityWithLabeledAttendees, requester?: Employee) => {
    const areExistingAttendees = "attendees" in existingData && existingData.attendees?.length > 0

    if (!areExistingAttendees && !requester) {
        return
    }

    if (areExistingAttendees && !requester) {
        return existingData.attendees
    }

    if (requester) {
        const requesterObject = { value: requester.id, label: requester.name }

        if (areExistingAttendees) {
            const containsRequester = existingData.attendees.find(({ value }) => value === requesterObject.value)
            return containsRequester ? existingData.attendees : [...existingData.attendees, requesterObject]
        }

        return [requesterObject]
    }

    return undefined
}

const shouldUpdatePricesForAutoGeneratedCardRequests = (expense: Expense, ocrObject: OcrResult) => {
    if (!ocrObject.totalNetPrice || !ocrObject.totalTaxPrice) {
        return false
    }

    const ocrSum = toAmountForCurrency(toDinero(Math.abs(ocrObject.totalTaxPrice)).add(toDinero(Math.abs(ocrObject.totalNetPrice))), ocrObject.currency || expense.currency)
    const expenseGrossPrice = Math.abs(expense.totalGrossPrice)

    return toDinero(ocrSum).equalsTo(toDinero(expenseGrossPrice))
}

// For autoGenerated card requests we disregard the OCR splits
// And if the OCR total net price + total tax price is not equal to the expense total gross price, it doesn't update "totalNetPrice", "totalTaxPrice", "taxRate" too
export const formatOcrObjectForAutoGeneratedCardRequests = (expense: Expense, ocrObject: OcrResult) => {
    const shouldUpdatePrices = shouldUpdatePricesForAutoGeneratedCardRequests(expense, ocrObject)

    const fieldsNotToBeUpdatedOnAutoGeneratedCardRequest = shouldUpdatePrices ? [] : ["totalNetPrice", "totalGrossPrice", "totalTaxPrice", "taxRate"]
    if (expense.splitType === SplitTypeEnum.SPLIT) {
        fieldsNotToBeUpdatedOnAutoGeneratedCardRequest.push("splits")
    }
    const ocrObjectToFormat = { ...ocrObject }

    for (const field of fieldsNotToBeUpdatedOnAutoGeneratedCardRequest) {
        delete ocrObjectToFormat[field as keyof OcrResult]
    }
    return formatOCRObject(ocrObjectToFormat)
}

export const getPreferredTaxByRate = ({ taxRate, taxes }: { taxRate: number; taxes: Array<Tax> }): Tax | undefined =>
    [...taxes].sort((tax) => (tax.isDefault ? -1 : 1)).find((tax) => tax.taxRate === taxRate && tax.deleted === false)

export const initializeOcrSettings = (loggedInProfile: Employee): OcrSettings => {
    const isOcrEnabled = loggedInProfile.settings.enableOcr
    const isOcrItemizationEnabled = loggedInProfile.settings.enableOcrItemization
    const isOcrItemizationDisplayed = true

    return {
        isOcrEnabled,
        isOcrItemizationEnabled,
        isOcrItemizationDisplayed,
    }
}

export const mapVendorReadFileAiResponseToVendor = (ocrData: VendorReadFileAiResponse | undefined): Partial<Vendor> | undefined => {
    const isExistingVendorDetected = ocrData?.vendorId
    if (isExistingVendorDetected) {
        return undefined
    }

    switch (ocrData?.ocrData?._discriminator) {
        case DocumentTypeEnum.PURCHASE_ORDER:
            const poSupplier = ocrData?.ocrData?.supplier

            return {
                name: poSupplier?.name,
                // tax
                vatNumber: poSupplier?.vatNumber,
                // bank
                iban: poSupplier?.ibanNumber,
                bic: poSupplier?.bicCode,
                // address
                streetName: poSupplier?.streetName,
                streetNumber: poSupplier?.streetNumber,
                city: poSupplier?.city,
                zip: poSupplier?.zipCode,
                // contact
                contactPersonName: poSupplier?.contactPerson,
                contactPersonPhone: poSupplier?.phoneNumber,
                contactPersonEmail: poSupplier?.email,
            }

        case DocumentTypeEnum.DELIVERY_NOTE:
            const dnSupplier = ocrData?.ocrData?.supplier

            return {
                name: dnSupplier?.name,
                vatNumber: dnSupplier?.vatId,
            }
        default:
            return undefined
    }
}
